import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The Direct Sale Lines matched to a Program Product are only linked through the Dir Sale Line Prog
 * Prod records the md module writes, so the matched Direct Sale Line ids are resolved first and then
 * used to filter the Direct Sale Lines. A Program Product accumulates matches across every Customer
 * and every period, so the match lookup is scoped up front - the ids become bind parameters on the
 * Sale Line query and Postgres caps a statement at 65,535 of them.
 */
export const ProgProdSaleLineList = ({ progProdId, custId, startDate, endDate }) => {
  const rsrc = useRsrc();
  const entityListRef = useRef();
  const [dirSaleLineIds, setDirSaleLineIds] = useState(null);

  useEffect(() => {
    let active = true;

    rsrc
      .rows('sc', 'DIR_SALE_LINE_PROG_PROD', {
        expr: ExprUtils.and(
          ExprUtils.is('progProdId', progProdId),
          custId ? ExprUtils.is('custId', custId) : null,
          // earnedDate is the Line's invoice date, so it is what the period boundaries apply to
          ExprUtils.effectiveDateClause('earnedDate', startDate, endDate),
        ),
      })
      .then((rows) => {
        if (active) setDirSaleLineIds(rows.map((row) => row.dirSaleLineId));
      });

    return () => {
      active = false;
    };
  }, [progProdId, custId, startDate, endDate, rsrc]);

  const totalsRow = () => {
    const gridRef = entityListRef.current.gridRef;

    const totals = { code: 'TOTAL', salesAmt: 0, netSalesAmt: 0, salesQty: 0 };

    const api = gridRef.current.gridApiRef.current;
    api.forEachNodeAfterFilterAndSort((node) => {
      totals.salesAmt = totals.salesAmt + (node.data.salesAmt ?? 0);
      totals.netSalesAmt = totals.netSalesAmt + (node.data.netSalesAmt ?? 0);
      totals.salesQty = totals.salesQty + (node.data.salesQty ?? 0);
    });
    totals.salesAmt = Math.round(totals.salesAmt * 100) / 100;
    totals.netSalesAmt = Math.round(totals.netSalesAmt * 100) / 100;
    totals.salesQty = Math.round(totals.salesQty * 1000) / 1000;

    gridRef.current.setTotalsRow(totals);
  };

  // Client side so that the totals cover every matched Sale Line rather than the scrolled page
  const clientSideRowData = useCallback(
    (setDataSource) => {
      if (dirSaleLineIds.length === 0) {
        setDataSource([]);
        totalsRow();
        return;
      }

      rsrc.rows('sc', 'DIR_SALE_LINE', { expr: ExprUtils.setExpr('id', dirSaleLineIds) }).then((rows) => {
        setDataSource(rows);
        totalsRow();
      });
    },
    [dirSaleLineIds, rsrc],
  );

  const augTabButtons = (tabButtons) => {
    ObjUtils.clearObj(tabButtons);
  };

  if (dirSaleLineIds == null) return null;

  return (
    <EntityList
      ref={entityListRef}
      module="sc"
      entity="DIR_SALE_LINE"
      clientSideRowData={clientSideRowData}
      augTabButtons={augTabButtons}
      events={{ onFilterChanged: totalsRow }}
    />
  );
};
